import { queryClient } from "@/client";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { HiOutlineCheckCircle } from "react-icons/hi2";

import { listDetailKey } from "@/hooks/api/list/useGetList";
import { listDataQueryKey } from "@/hooks/api/list/useInfiniteListsDetail";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import { useToast } from "@/hooks/useToast";
import { UnmatchedFormValues } from "@/components/molecules/modals/matching/MapUnmatchedItemsModal";

type DiscardUnmatchedMutation = {
  listId: string;
} & UnmatchedFormValues;

export const useMatchUnmatched = () => {
  const { apiClient } = useSalesyApiClient();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const processItems = ({ items }: UnmatchedFormValues): { company_id: string; entry_id: string }[] => {
    const processedItems =
      items &&
      Object.entries(items)
        .filter(([, company]) => company !== null && company !== undefined)
        .map(([entry_id, company]) => ({
          entry_id,
          company_id: company!.id,
        }));

    return processedItems ?? [];
  };

  const match = (requestBody: DiscardUnmatchedMutation) => {
    const convertedItems = processItems({ items: requestBody.items });

    return apiClient.listsEnrichment.matchUnmatchedEntries({
      id: requestBody.listId,
      requestBody: { items: convertedItems },
    });
  };

  const mutation = useMutation({
    mutationFn: (requestBody: DiscardUnmatchedMutation) => match(requestBody),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [listDataQueryKey, { id: variables.listId }] });
      queryClient.invalidateQueries({ queryKey: listDetailKey(variables.listId) });

      showToast({
        featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
        heading: t("unmatched.success", {
          count: Object.entries(variables.items).filter(([, company_id]) => company_id !== null).length,
        }),
      });
    },
  });

  const { mutate, ...mutationResult } = mutation;

  return {
    ...mutationResult,
    matchUnmatched: mutate,
  };
};
